import React, { useState, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  FiSearch, 
  FiFilter, 
  FiChevronLeft, 
  FiChevronRight, 
  FiChevronsLeft, 
  FiChevronsRight,
  FiEye,
  FiEdit,
  FiTrash2,
  FiMoreVertical,
  FiArrowUp,
  FiArrowDown,
  FiDownload,
  FiShare2,
  FiRefreshCw,
  FiSend,
  FiBarChart2,
  FiSettings
} from 'react-icons/fi';
import EmailMasterLoader from './EmailMasterLoader';

const EnhancedUniversalTable = ({
  // Data and Structure
  data = [],
  columns = [],
  loading = false,
  loadingMessage = "Loading...",
  
  // Features
  searchable = true,
  filterable = true,
  sortable = true,
  pagination = true,
  pageSize = 10,
  actions = [],
  onRowClick = null,
  onActionClick = null,
  
  // Backend Sorting Support
  sortConfig = null, // External sort configuration
  onSort = null, // External sort handler
  
  // Styling
  searchPlaceholder = "Search...",
  emptyMessage = "No data available",
  className = "",
  style = {},
  striped = true,
  hover = true,
  bordered = false,
  responsive = true,
  size = "md", // sm, md, lg
  variant = "default", // default, primary, secondary, success, danger, warning, info, light, dark
  compact = false,
  showHeader = true,
  stickyHeader = false,
  
  // Advanced Features
  selectable = false,
  selectedItems = [],
  onSelectionChange = null,
  bulkActions = [],
  showRowNumbers = false,
  rowHeight = "auto"
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [internalSortConfig, setInternalSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({});
  const [selectedRows, setSelectedRows] = useState(selectedItems);
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);
  const searchRef = useRef(null);

  // Close search suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchSuggestions(false);
      }
    };

    if (showSearchSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSearchSuggestions]);

  // Filter and search data
  const filteredData = useMemo(() => {
    let filtered = data;

    // Apply search
    if (searchTerm && searchable) {
      filtered = filtered.filter(row =>
        columns.some(column => {
          const value = row[column.key];
          return value && value.toString().toLowerCase().includes(searchTerm.toLowerCase());
        })
      );
    }

    // Apply filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== 'all') {
        filtered = filtered.filter(row => {
          const cellValue = row[key];
          return cellValue && cellValue.toString().toLowerCase().includes(value.toLowerCase());
        });
      }
    });

    return filtered;
  }, [data, searchTerm, filters, columns, searchable]);

  // Sort data
  const sortedData = useMemo(() => {
    // If external sort config is provided (backend sorting), data is already sorted by backend
    if (sortConfig && onSort) {
      return filteredData;
    }
    
    // Otherwise use internal sorting (frontend sorting)
    if (!internalSortConfig.key || !sortable) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aValue = a[internalSortConfig.key];
      const bValue = b[internalSortConfig.key];

      if (aValue < bValue) {
        return internalSortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return internalSortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredData, sortConfig, internalSortConfig, sortable, onSort]);

  // Paginate data
  const paginatedData = useMemo(() => {
    if (!pagination) return sortedData;

    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, currentPage, pageSize, pagination]);

  // Calculate pagination info
  const totalPages = Math.ceil(filteredData.length / pageSize);
  const totalItems = filteredData.length;
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  // Handle sorting
  const handleSort = (key) => {
    if (!sortable) return;

    // If external sort handler is provided, use it (backend sorting)
    if (onSort) {
      // Pass the column key directly to the external sort handler
      onSort(key);
      return;
    }

    // Otherwise use internal sorting (frontend sorting)
    setInternalSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Get current sort configuration for display
  const getCurrentSortConfig = () => {
    if (sortConfig) {
      return {
        key: sortConfig.sortField,
        direction: sortConfig.sortOrder
      };
    }
    return internalSortConfig;
  };

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setCurrentPage(1);
  };

  // Handle search change
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
    setShowSearchSuggestions(value.length > 0);
  };

  // Get search suggestions
  const getSearchSuggestions = () => {
    if (!searchTerm || searchTerm.length < 2) return [];
    
    const suggestions = new Set();
    columns.forEach(column => {
      data.forEach(row => {
        const value = row[column.key];
        if (value && value.toString().toLowerCase().includes(searchTerm.toLowerCase())) {
          suggestions.add(value.toString());
        }
      });
    });
    
    return Array.from(suggestions).slice(0, 5);
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Get table size class
  const getTableSizeClass = () => {
    switch (size) {
      case 'sm': return 'table-sm';
      case 'lg': return 'table-lg';
      default: return '';
    }
  };

  // Get table variant class
  const getTableVariantClass = () => {
    switch (variant) {
      case 'primary': return 'table-primary';
      case 'secondary': return 'table-secondary';
      case 'success': return 'table-success';
      case 'danger': return 'table-danger';
      case 'warning': return 'table-warning';
      case 'info': return 'table-info';
      case 'light': return 'table-light';
      case 'dark': return 'table-dark';
      default: return '';
    }
  };

  // Render cell content
  const renderCellContent = (row, column, rowIndex) => {
    const value = row[column.key];
    
    if (column.render) {
      return column.render(value, row, rowIndex);
    }
    
    if (column.type === 'status') {
      const statusConfig = {
        active: { bg: '#10b981', text: '#ffffff', icon: '✓' },
        inactive: { bg: '#6b7280', text: '#ffffff', icon: '○' },
        pending: { bg: '#f59e0b', text: '#ffffff', icon: '⏳' },
        completed: { bg: '#3b82f6', text: '#ffffff', icon: '✓' },
        failed: { bg: '#ef4444', text: '#ffffff', icon: '✗' },
        sent: { bg: '#10b981', text: '#ffffff', icon: '✓' },
        sending: { bg: '#8b5cf6', text: '#ffffff', icon: '↗' },
        draft: { bg: '#6b7280', text: '#ffffff', icon: '📝' },
        success: { bg: '#10b981', text: '#ffffff', icon: '✓' },
        error: { bg: '#ef4444', text: '#ffffff', icon: '✗' },
        warning: { bg: '#f59e0b', text: '#ffffff', icon: '⚠' },
        info: { bg: '#3b82f6', text: '#ffffff', icon: 'ℹ' },
        approved: { bg: '#10b981', text: '#ffffff', icon: '✓' },
        rejected: { bg: '#ef4444', text: '#ffffff', icon: '✗' },
        processing: { bg: '#3b82f6', text: '#ffffff', icon: '⚙' }
      };
      
      const config = statusConfig[value?.toLowerCase()] || statusConfig.inactive;
      
      return (
        <span 
          className="badge badge-pill"
          style={{
            background: `linear-gradient(135deg, ${config.bg} 0%, ${config.bg}dd 100%)`,
            color: config.text,
            padding: compact ? '4px 10px' : '6px 12px',
            borderRadius: '20px',
            fontSize: compact ? '0.7rem' : '0.75rem',
            fontWeight: '600',
            textTransform: 'capitalize',
            boxShadow: `0 2px 8px ${config.bg}40`,
            border: `1px solid ${config.bg}20`,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            letterSpacing: '0.5px'
          }}
        >
          <span style={{ fontSize: '0.8em' }}>{config.icon}</span>
          {value}
        </span>
      );
    }
    
    if (column.type === 'date') {
      return new Date(value).toLocaleDateString();
    }
    
    if (column.type === 'datetime') {
      return new Date(value).toLocaleString();
    }
    
    if (column.type === 'number') {
      return value?.toLocaleString();
    }
    
    if (column.type === 'currency') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(value);
    }
    
    if (column.type === 'email') {
      return (
        <span style={{ 
          color: '#007bff', 
          textDecoration: 'none',
          fontSize: compact ? '0.85rem' : '0.9rem'
        }}>
          {value}
        </span>
      );
    }
    
    if (column.type === 'name') {
      return (
        <span style={{ 
          fontWeight: '500',
          fontSize: compact ? '0.85rem' : '0.9rem'
        }}>
          {value}
        </span>
      );
    }
    
    // Handle objects and arrays
    if (typeof value === 'object' && value !== null) {
      if (Array.isArray(value)) {
        return value.length > 0 ? `${value.length} items` : 'Empty';
      }
      return JSON.stringify(value);
    }
    
    return value || '-';
  };

   // Dropdown Menu Component
   const ActionDropdown = ({ row, rowIndex, actions }) => {
     const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const buttonRef = useRef(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });

     // Close dropdown when clicking outside
     useEffect(() => {
       const handleClickOutside = (event) => {
         if (dropdownRef.current && !dropdownRef.current.contains(event.target) && 
             buttonRef.current && !buttonRef.current.contains(event.target)) {
           setIsOpen(false);
         }
       };

       if (isOpen) {
         document.addEventListener('mousedown', handleClickOutside);
       }

       return () => {
         document.removeEventListener('mousedown', handleClickOutside);
       };
     }, [isOpen]);

    // Filter actions based on show condition
    const visibleActions = actions.filter(action => {
      if (typeof action.show === 'function') {
        return action.show(row);
      }
      return action.show !== false;
    });

    if (visibleActions.length === 0) return null;

    return (
      <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-block', zIndex: 9999 }}>
        <button
          ref={buttonRef}
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={(e) => {
            e.stopPropagation();
            if (!isOpen && buttonRef.current) {
              const rect = buttonRef.current.getBoundingClientRect();
              setDropdownPosition({
                top: rect.bottom + window.scrollY + 8,
                right: window.innerWidth - rect.right - window.scrollX
              });
            }
            setIsOpen(!isOpen);
          }}
          style={{
            padding: '8px',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            position: 'relative',
            overflow: 'hidden',
            zIndex: 9999
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)';
            e.target.style.transform = 'translateY(-2px) scale(1.05)';
            e.target.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)';
            e.target.style.transform = 'translateY(0) scale(1)';
            e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
          }}
        >
          <FiMoreVertical style={{ 
            color: '#6b7280', 
            fontSize: '18px',
            transition: 'transform 0.3s ease'
          }} />
          
          {/* Animated background effect */}
          <div style={{
            position: 'absolute',
            top: '0',
            left: '0',
            right: '0',
            bottom: '0',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            opacity: '0',
            transition: 'opacity 0.3s ease',
            borderRadius: '12px'
          }}></div>
        </button>

         {isOpen && createPortal(
           <div
             ref={dropdownRef}
             style={{
               position: 'fixed',
               top: dropdownPosition.top,
               right: dropdownPosition.right,
               background: 'white',
               borderRadius: '16px',
               boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
               border: '1px solid rgba(0, 0, 0, 0.1)',
               zIndex: 9999,
               minWidth: '200px',
               overflow: 'hidden',
               padding: '0'
             }}
           >
            {visibleActions.map((action, index) => {
              const variantColors = {
                primary: { bg: '#eff6ff', text: '#2563eb', icon: '#3b82f6' },
                secondary: { bg: '#f9fafb', text: '#374151', icon: '#6b7280' },
                success: { bg: '#f0fdf4', text: '#16a34a', icon: '#22c55e' },
                danger: { bg: '#fef2f2', text: '#dc2626', icon: '#ef4444' },
                warning: { bg: '#fffbeb', text: '#d97706', icon: '#f59e0b' },
                info: { bg: '#f0f9ff', text: '#0284c7', icon: '#0ea5e9' }
              };
              
              const colors = variantColors[action.variant] || variantColors.secondary;
              
              return (
                <button
                  key={index}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onActionClick && onActionClick(action.key, row, rowIndex);
                    setIsOpen(false);
                  }}
                  disabled={action.disabled && action.disabled(row)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    width: '100%',
                    padding: '12px 16px',
                    background: 'none',
                    border: 'none',
                    color: '#374151',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    textAlign: 'left',
                    borderBottom: index < visibleActions.length - 1 ? '1px solid #f3f4f6' : 'none'
                  }}
                >
                  {action.icon || <FiMoreVertical />} {action.title}
                </button>
              );
            })}
          </div>,
          document.body
        )}
      </div>
    );
  };

  // Render action buttons
  const renderActions = (row, rowIndex) => {
    if (!actions || actions.length === 0) return null;

    return <ActionDropdown row={row} rowIndex={rowIndex} actions={actions} />;
  };

  // Don't return early for loading - we'll handle it in the render

  return (
    <div 
      className={`enhanced-universal-table ${className}`} 
      style={{
        backgroundColor: '#ffffff',
        borderRadius: '0',
        boxShadow: 'none',
        overflow: 'hidden',
        border: 'none',
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        margin: '0 auto',
        ...style
      }}
    >
      {/* Enhanced Header Controls */}
      <div 
        style={{ 
          padding: '20px 24px',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          width: '100%',
          margin: '0 auto',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          position: 'relative'
        }}
      >
        {/* Top Row - Search and Quick Actions */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          flexWrap: 'wrap', 
          gap: '16px',
          '@media (max-width: 768px)': {
            flexDirection: 'column',
            alignItems: 'stretch'
          }
        }}>
          {/* Search Section */}
          {searchable && (
            <div ref={searchRef} style={{ 
              position: 'relative', 
              minWidth: '280px', 
              flex: '1', 
              maxWidth: '500px',
              width: '100%'
            }}>
              <div style={{ position: 'relative' }}>
                <FiSearch 
                  style={{ 
                    position: 'absolute',
                    left: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: searchTerm ? '#3b82f6' : '#9ca3af',
                    fontSize: '18px',
                    transition: 'color 0.3s ease',
                    zIndex: 2
                  }} 
                />
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                style={{
                    width: '100%',
                    padding: '14px 16px 14px 48px',
                    fontSize: compact ? '0.95rem' : '1rem',
                    borderRadius: '16px',
                    border: '2px solid #e5e7eb',
                  backgroundColor: '#ffffff',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                    outline: 'none',
                    fontWeight: '400'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 4px rgba(59, 130, 246, 0.1), 0 8px 25px rgba(0, 0, 0, 0.1)';
                    e.target.style.transform = 'translateY(-2px)';
                }}
                onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
                    e.target.style.transform = 'translateY(0)';
                  }}
                />
                {searchTerm && (
                  <button
                    onClick={() => handleSearchChange('')}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                      border: 'none',
                      color: '#ffffff',
                      cursor: 'pointer',
                      padding: '6px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s ease',
                      fontSize: '14px',
                      width: '24px',
                      height: '24px',
                      boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-50%) scale(1.1)';
                      e.target.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(-50%) scale(1)';
                      e.target.style.boxShadow = '0 2px 8px rgba(239, 68, 68, 0.3)';
                    }}
                  >
                    ×
                  </button>
                )}
                
                {/* Search Suggestions Dropdown */}
                {showSearchSuggestions && getSearchSuggestions().length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: '0',
                    right: '0',
                    backgroundColor: '#ffffff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
                    zIndex: 1000,
                    marginTop: '4px',
                    overflow: 'hidden'
                  }}>
                    {getSearchSuggestions().map((suggestion, index) => (
                      <div
                        key={index}
                        onClick={() => {
                          handleSearchChange(suggestion);
                          setShowSearchSuggestions(false);
                        }}
                        style={{
                          padding: '12px 16px',
                          cursor: 'pointer',
                          borderBottom: index < getSearchSuggestions().length - 1 ? '1px solid #f3f4f6' : 'none',
                          transition: 'background-color 0.2s ease',
                          fontSize: '0.9rem',
                          color: '#374151'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#f8fafc';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FiSearch style={{ color: '#9ca3af', fontSize: '14px' }} />
                          <span>{suggestion}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Results and Actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div 
              style={{ 
                fontSize: compact ? '0.85rem' : '0.9rem',
                color: '#374151',
                fontWeight: '600',
                padding: '10px 16px',
                backgroundColor: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                borderRadius: '12px',
                border: '1px solid #d1d5db',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: totalItems > 0 ? '#10b981' : '#6b7280'
              }}></div>
              {totalItems} {totalItems === 1 ? 'result' : 'results'}
            </div>
          </div>
        </div>

        {/* Filter Section */}
          {filterable && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FiFilter style={{ color: '#6b7280', fontSize: '16px' }} />
              <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#374151' }}>Filters:</span>
            </div>
            
              {columns.filter(col => col.filterable !== false).map((column, index) => (
              <div key={index} style={{ position: 'relative' }}>
                <div style={{ position: 'relative' }}>
                <select
                  value={filters[column.key] || 'all'}
                  onChange={(e) => handleFilterChange(column.key, e.target.value)}
                  style={{
                      padding: '10px 16px 10px 16px',
                      fontSize: compact ? '0.85rem' : '0.9rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      backgroundColor: filters[column.key] && filters[column.key] !== 'all' ? '#eff6ff' : '#ffffff',
                    minWidth: '140px',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      cursor: 'pointer',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)',
                      outline: 'none',
                      appearance: 'none',
                      fontWeight: '500',
                      color: filters[column.key] && filters[column.key] !== 'all' ? '#1d4ed8' : '#374151',
                      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                      backgroundPosition: 'right 12px center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '16px'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                      e.target.style.boxShadow = '0 0 0 4px rgba(59, 130, 246, 0.1), 0 4px 12px rgba(0, 0, 0, 0.08)';
                      e.target.style.transform = 'translateY(-1px)';
                  }}
                  onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.04)';
                      e.target.style.transform = 'translateY(0)';
                  }}
                >
                  <option value="all">All {column.title}</option>
                  {[...new Set(data.map(row => row[column.key]))].map(value => {
                    const displayValue = typeof value === 'object' && value !== null 
                      ? JSON.stringify(value) 
                      : value || 'Empty';
                    return (
                      <option key={displayValue} value={displayValue}>{displayValue}</option>
                    );
                  })}
                </select>
                  
                  {/* Active Filter Indicator */}
                  {filters[column.key] && filters[column.key] !== 'all' && (
                    <div style={{
                      position: 'absolute',
                      top: '-4px',
                      right: '-4px',
                      width: '12px',
                      height: '12px',
                      backgroundColor: '#3b82f6',
                      borderRadius: '50%',
                      border: '2px solid #ffffff',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                    }}></div>
                  )}
                </div>
              </div>
            ))}
            
            {/* Clear All Filters Button */}
            {Object.values(filters).some(filter => filter && filter !== 'all') && (
              <button
                onClick={() => setFilters({})}
                style={{
                  padding: '8px 12px',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  color: '#ef4444',
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#fee2e2';
                  e.target.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#fef2f2';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                Clear All
              </button>
            )}
            </div>
          )}
      </div>

      {/* Table */}
      <div className={responsive ? "table-responsive" : ""} style={{ 
        flex: 1, 
        overflow: 'auto', 
        height: 'calc(100% - 40px)', 
        width: '100%', 
        margin: '0 auto',
        border: '1px solid #e5e7eb',
        borderRadius: '8px'
      }}>
        <table 
          className={`table ${getTableSizeClass()} ${getTableVariantClass()} ${
            striped ? 'table-striped' : ''
          } ${bordered ? 'table-bordered' : ''} mb-0`}
          style={{
            marginBottom: '0',
            fontSize: compact ? '0.85rem' : '0.9rem',
            width: '100%',
            minWidth: '800px', // Minimum width to ensure proper column sizing
            tableLayout: 'fixed' // Fixed table layout for consistent column widths
          }}
        >
          {showHeader && (
            <thead 
              style={{
                position: stickyHeader ? 'sticky' : 'static',
                top: '0',
                zIndex: '10',
                backgroundColor: '#f9fafb',
                borderBottom: '2px solid #e5e7eb',
                width: '100%'
              }}
            >
              <tr>
                {showRowNumbers && (
                  <th scope="col" style={{ width: '50px', textAlign: 'center' }}>#</th>
                )}
                {selectable && (
                  <th scope="col" style={{ width: '50px', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={selectedRows.length === paginatedData.length && paginatedData.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedRows(paginatedData.map((_, index) => index));
                        } else {
                          setSelectedRows([]);
                        }
                      }}
                    />
                  </th>
                )}
                {columns.map((column, index) => (
                  <th
                    key={index}
                    scope="col"
                    style={{
                      cursor: sortable ? 'pointer' : 'default',
                      userSelect: 'none',
                      position: 'relative',
                      padding: compact ? '5px 6px' : '6px 8px',
                      fontSize: compact ? '0.75rem' : '0.8rem',
                      fontWeight: '600',
                      color: '#374151',
                      borderBottom: 'none',
                      width: column.width || 'auto', // Use column width if specified
                      minWidth: column.minWidth || '120px', // Minimum width for readability
                      maxWidth: column.maxWidth || 'none'
                    }}
                    onClick={() => handleSort(column.key)}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <span>{column.title}</span>
                      {sortable && getCurrentSortConfig().key === column.key && (
                        <span style={{ fontSize: '0.8rem', marginLeft: '5px' }}>
                          {getCurrentSortConfig().direction === 'asc' ? <FiArrowUp /> : <FiArrowDown />}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
                {actions && actions.length > 0 && (
                  <th 
                    scope="col" 
                    style={{ 
                      width: '80px',
                      padding: compact ? '8px 12px' : '12px 15px',
                      fontSize: compact ? '0.8rem' : '0.85rem',
                      fontWeight: '600',
                      textAlign: 'center'
                    }}
                  >
                    Actions
                  </th>
                )}
              </tr>
            </thead>
          )}
          <tbody style={{ width: '100%' }}>
            {loading ? (
              <tr>
                <td 
                  colSpan={columns.length + (actions && actions.length > 0 ? 1 : 0) + (selectable ? 1 : 0) + (showRowNumbers ? 1 : 0)}
                  style={{ 
                    padding: '0',
                    border: 'none'
                  }}
                >
                  <EmailMasterLoader message={loadingMessage} />
                </td>
              </tr>
            ) : paginatedData.length > 0 ? (
              paginatedData.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  style={{
                    cursor: onRowClick ? 'pointer' : 'default',
                    transition: 'all 0.2s ease',
                    height: rowHeight !== 'auto' ? rowHeight : '35px',
                    borderBottom: '1px solid #f3f4f6'
                  }}
                  onClick={() => onRowClick && onRowClick(row, rowIndex)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f8fafc';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.transition = 'all 0.2s ease';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '';
                    e.currentTarget.style.boxShadow = '';
                    e.currentTarget.style.transform = '';
                    e.currentTarget.style.transition = 'all 0.2s ease';
                  }}
                >
                  {showRowNumbers && (
                    <td style={{ textAlign: 'center', fontWeight: '500', color: '#6c757d' }}>
                      {startItem + rowIndex}
                    </td>
                  )}
                  {selectable && (
                    <td style={{ textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(rowIndex)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedRows([...selectedRows, rowIndex]);
                          } else {
                            setSelectedRows(selectedRows.filter(i => i !== rowIndex));
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                  )}
                  {columns.map((column, colIndex) => (
                    <td 
                      key={colIndex}
                      style={{
                        padding: compact ? '4px 6px' : '5px 8px',
                        verticalAlign: 'middle',
                        borderTop: '1px solid #dee2e6',
                        fontSize: compact ? '0.75rem' : '0.8rem',
                        width: column.width || 'auto',
                        minWidth: column.minWidth || '120px',
                        maxWidth: column.maxWidth || 'none',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {renderCellContent(row, column, rowIndex)}
                    </td>
                  ))}
                  {actions && actions.length > 0 && (
                    <td 
                      style={{
                        padding: compact ? '4px 6px' : '5px 8px',
                        verticalAlign: 'middle',
                        borderTop: '1px solid #dee2e6',
                        textAlign: 'center',
                        fontSize: compact ? '0.75rem' : '0.8rem'
                      }}
                    >
                      {renderActions(row, rowIndex)}
                    </td>
                  )}
                </tr>
              ))
            ) : !loading ? (
              <tr>
                <td 
                  colSpan={columns.length + (actions && actions.length > 0 ? 1 : 0) + (selectable ? 1 : 0) + (showRowNumbers ? 1 : 0)}
                  className="text-center text-muted"
                  style={{ 
                    padding: '2rem',
                    fontSize: compact ? '0.85rem' : '0.9rem',
                    color: '#6c757d'
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <FiSearch style={{ fontSize: '2rem', marginBottom: '10px', opacity: 0.5 }} />
                    {emptyMessage}
                  </div>
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && totalPages > 1 && (
        <div 
          className="d-flex justify-content-between align-items-center"
          style={{
            padding: '16px 20px',
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            borderTop: '1px solid #e2e8f0',
            marginTop: 'auto',
            minHeight: '60px',
            width: '100%',
            margin: '0 auto',
            boxShadow: '0 -1px 3px rgba(0, 0, 0, 0.05)'
          }}
        >
          <div 
            style={{ 
              fontSize: compact ? '0.85rem' : '0.9rem',
              color: '#6b7280',
              fontWeight: '500',
              padding: '8px 12px',
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)'
            }}
          >
            Showing {startItem} to {endItem} of {totalItems} entries
          </div>
          
          <nav>
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  style={{
                    padding: compact ? '4px 8px' : '6px 12px',
                    fontSize: compact ? '0.75rem' : '0.8rem'
                  }}
                >
                  <FiChevronsLeft />
                </button>
              </li>
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  style={{
                    padding: compact ? '4px 8px' : '6px 12px',
                    fontSize: compact ? '0.75rem' : '0.8rem'
                  }}
                >
                  <FiChevronLeft />
                </button>
              </li>
              
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(pageNum)}
                      style={{
                        padding: compact ? '4px 8px' : '6px 12px',
                        fontSize: compact ? '0.75rem' : '0.8rem'
                      }}
                    >
                      {pageNum}
                    </button>
                  </li>
                );
              })}
              
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: compact ? '4px 8px' : '6px 12px',
                    fontSize: compact ? '0.75rem' : '0.8rem'
                  }}
                >
                  <FiChevronRight />
                </button>
              </li>
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: compact ? '4px 8px' : '6px 12px',
                    fontSize: compact ? '0.75rem' : '0.8rem'
                  }}
                >
                  <FiChevronsRight />
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </div>
  );
};

export default EnhancedUniversalTable;
